import React from 'react';

// What is on the screen when a render throws.
//
// Until this existed, the answer was nothing: React unmounts the whole tree when a component
// throws during render, so one unguarded `preview.totals.gross_cents` on a statement that had no
// totals took out the header, the sidebar and every other page with it. What the person saw was
// a white page — no error, nothing to press, no clue which of a hundred screens had done it, and
// nothing to tell anybody except "it went blank".
//
// This does not make the crash not a crash. It makes it a crash you can describe: what broke,
// where, and a way back to the rest of the app that does not involve knowing to reload.
// A page's code that no longer exists on the server. After a deploy the old build's chunks are
// gone, so the first page somebody opens that they had not opened yet fails to load — and that is
// not a crash, it is the app being out of date. It gets a small refresh prompt, not the crash card.
export const isStaleBuild = (error) => {
  const s = `${(error && error.name) || ''} ${(error && error.message) || error || ''}`;
  return /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(s);
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Still logged, because the stack is what somebody debugging actually wants and the panel
    // below is a summary. The console is where they will look first.
    console.error('Render failed:', error, info && info.componentStack);
    this.setState({ info });
  }

  render() {
    if (!this.state.error) return this.props.children;

    const { error, info } = this.state;
    if (isStaleBuild(error)) {
      return (
        <div className="stale-build" role="alert">
          <span>✨ The Hub has been updated. Refresh to load the new version — nothing you were doing is lost.</span>
          <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>Refresh</button>
        </div>
      );
    }
    const where = (info && info.componentStack ? info.componentStack : '')
      .split('\n').map(l => l.trim()).filter(Boolean).slice(0, 6).join('\n');

    return (
      <div className="crash">
        <div className="crash-card">
          <div className="crash-title">This screen stopped working</div>
          <p className="crash-note">
            Nothing you did was wrong and nothing has been lost — this page hit an error while drawing
            itself. The rest of the Hub is fine.
          </p>
          <div className="crash-actions">
            <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>Reload the page</button>
            {/* Assigned rather than routed: the router is inside the tree that just threw. */}
            <button className="btn btn-ghost btn-sm" onClick={() => { window.location.href = '/'; }}>Go to Work</button>
          </div>
          {/* The part worth copying into a bug report. Kept open rather than behind a toggle,
              because the one thing that was missing before was something to paste. */}
          <div className="crash-detail">
            <div className="crash-detail-head">What went wrong</div>
            <pre>{String((error && error.stack) || error)}</pre>
            {where && <><div className="crash-detail-head">Where</div><pre>{where}</pre></>}
          </div>
        </div>
      </div>
    );
  }
}
